import {
  Button,
  IconButton,
  Paper,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import HistoryIcon from '@mui/icons-material/History';
import { TypeColors } from '@mui/material/styles';
import { FC, useEffect, useMemo } from 'react';
import { ILeftColumn } from '../../leftColumn.component';
import { InfoTipByCustomer } from '../../../../../infoTipByCustomer/infoTipByCustomer.component';
import { EmailTrackingPopup } from '../../../../../emailTracking/components/emailTracking.popup';
import { useTranslation } from 'react-i18next';

export const CustomerHeader: FC<TCustomerHeader> = (props) => {
  const { customerName, infoTip, emailTip } = props;
  const { t } = useTranslation();
  const emailTrackingPopup = useMemo(() => new EmailTrackingPopup(), []);

  useEffect(() => {
    return () => emailTrackingPopup.stream.unsubscribe();
  }, []);

  return (
    <Stack
      direction='row'
      spacing='6px'
      sx={{
        alignItems: 'center',
      }}
    >
      <STitle variant='h5'>{customerName}</STitle>
      <InfoTipByCustomer {...{ ...infoTip }} />
      {emailTip && (
        <STooltipWrapper
          title={
            <SInfoContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                pt: '5px',
                pb: '5px',
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: 14,
                }}
              >
                {emailTip.message}
              </Typography>
              {emailTip.params && (
                <Button
                  onClick={() =>
                    emailTrackingPopup.stream.emit('open', {
                      params: { queryParams: emailTip.params },
                    })
                  }
                  sx={{ ml: '6px' }}
                  startIcon={<HistoryIcon />}
                >
                  {t('common.track')}
                </Button>
              )}
            </SInfoContent>
          }
          placement='right'
        >
          <SIconButton colorBtn='darkGreen'>
            <MarkEmailReadIcon />
          </SIconButton>
        </STooltipWrapper>
      )}
      {emailTip && <emailTrackingPopup.Component />}
    </Stack>
  );
};

const STooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 0,
    maxWidth: '500px',
  },
}));

const STitle = styled(Typography)(() => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const SInfoContent = styled(Paper)(() => ({
  padding: 10,
  fontSize: 14,
}));

const SIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'colorBtn',
})<{ colorBtn?: TypeColors }>(({ theme, colorBtn }) => ({
  padding: 4,
  color: `${theme.palette[colorBtn || 'lightBlue'].main}90`,
  '&:hover': {
    backgroundColor: `${theme.palette[colorBtn || 'lightBlue'].main}10`,
  },
}));

type TCustomerHeader = Pick<ILeftColumn, 'customerName' | 'infoTip' | 'emailTip'>;
